import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { 
  Share2, ZoomIn, ZoomOut, Maximize2, Download, RefreshCw,
  DollarSign, Cloud, Server, Database, Cpu, HardDrive,
  AlertTriangle, TrendingUp, TrendingDown, Info, Filter,
  ChevronRight, ChevronDown, ArrowRight, Sparkles, Target,
  Layers, GitBranch, Network, Box, Activity, Eye, EyeOff,
  Play, Pause, RotateCcw, Settings, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CostIntelligenceGraph = () => {
  const { shouldShowData } = useDemoData();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [viewMode, setViewMode] = useState('interactive');
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root', 'aws', 'azure', 'gcp', 'snowflake', 'databricks', 'digitalocean']));
  const [animating, setAnimating] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterMinCost, setFilterMinCost] = useState(0);
  const [timeRange, setTimeRange] = useState('mtd');
  const [drillPath, setDrillPath] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    fetchGraphData();
  }, [timeRange]);

  useEffect(() => {
    let interval;
    if (animating) {
      interval = setInterval(() => {
        setAnimationStep(prev => (prev + 1) % 100);
      }, 50);
    }
    return () => clearInterval(interval);
  }, [animating]);

  const fetchGraphData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard-builder/widget-data/cost-intelligence-graph`);
      setGraphData(response.data.data);
    } catch (error) {
      // Enhanced mock data with more depth
      setGraphData({
        nodes: [
          { id: 'root', label: 'Total Cloud Spend', value: 14409.50, type: 'total', trend: 5.4, anomaly: false },
          // Providers
          { id: 'aws', label: 'AWS', value: 7182.35, type: 'provider', parent: 'root', color: '#FF9900', trend: 8.2, anomaly: false },
          { id: 'azure', label: 'Azure', value: 2134.89, type: 'provider', parent: 'root', color: '#0078D4', trend: -3.1, anomaly: false },
          { id: 'gcp', label: 'GCP', value: 1092.26, type: 'provider', parent: 'root', color: '#4285F4', trend: 12.5, anomaly: true },
          { id: 'snowflake', label: 'Snowflake', value: 4000.00, type: 'provider', parent: 'root', color: '#29B5E8', trend: 15.3, anomaly: false },
          { id: 'databricks', label: 'Databricks', value: 2850.00, type: 'provider', parent: 'root', color: '#FF3621', trend: 22.7, anomaly: true },
          { id: 'digitalocean', label: 'DigitalOcean', value: 945.50, type: 'provider', parent: 'root', color: '#0080FF', trend: 4.1, anomaly: false },
          // AWS Services
          { id: 'ec2', label: 'EC2', value: 3245.67, type: 'service', parent: 'aws', color: '#FF9900', trend: 15.2, anomaly: true, description: 'Elastic Compute Cloud' },
          { id: 'rds', label: 'RDS', value: 2134.89, type: 'service', parent: 'aws', color: '#FF9900', trend: 2.1, anomaly: false, description: 'Relational Database' },
          { id: 's3', label: 'S3', value: 1234.56, type: 'service', parent: 'aws', color: '#FF9900', trend: -5.3, anomaly: false, description: 'Simple Storage' },
          { id: 'lambda', label: 'Lambda', value: 567.23, type: 'service', parent: 'aws', color: '#FF9900', trend: 25.0, anomaly: false, description: 'Serverless Compute' },
          // Azure Services  
          { id: 'vms', label: 'Virtual Machines', value: 987.65, type: 'service', parent: 'azure', color: '#0078D4', trend: -8.2, anomaly: false },
          { id: 'sql', label: 'SQL Database', value: 567.24, type: 'service', parent: 'azure', color: '#0078D4', trend: 1.5, anomaly: false },
          { id: 'storage', label: 'Blob Storage', value: 580.00, type: 'service', parent: 'azure', color: '#0078D4', trend: 3.2, anomaly: false },
          // GCP Services
          { id: 'bigquery', label: 'BigQuery', value: 654.32, type: 'service', parent: 'gcp', color: '#4285F4', trend: 18.5, anomaly: true },
          { id: 'compute', label: 'Compute Engine', value: 437.94, type: 'service', parent: 'gcp', color: '#4285F4', trend: 5.1, anomaly: false },
          // Snowflake Services
          { id: 'sf-warehouse', label: 'Warehouse Compute', value: 2500.00, type: 'service', parent: 'snowflake', color: '#29B5E8', trend: 18.2, anomaly: false, description: 'Compute Credits' },
          { id: 'sf-storage', label: 'Storage', value: 1000.00, type: 'service', parent: 'snowflake', color: '#29B5E8', trend: 5.5, anomaly: false, description: 'Data Storage' },
          { id: 'sf-transfer', label: 'Data Transfer', value: 500.00, type: 'service', parent: 'snowflake', color: '#29B5E8', trend: 12.0, anomaly: false, description: 'Egress Costs' },
          // Databricks Services
          { id: 'db-allpurpose', label: 'All-Purpose Compute', value: 1450.00, type: 'service', parent: 'databricks', color: '#FF3621', trend: 28.3, anomaly: true, description: 'Interactive DBUs' },
          { id: 'db-jobs', label: 'Jobs Compute', value: 980.00, type: 'service', parent: 'databricks', color: '#FF3621', trend: 12.1, anomaly: false, description: 'Scheduled Jobs DBUs' },
          { id: 'db-sqlwh', label: 'SQL Warehouses', value: 420.00, type: 'service', parent: 'databricks', color: '#FF3621', trend: 9.8, anomaly: false, description: 'Photon-enabled' },
          // DigitalOcean Services
          { id: 'do-droplets', label: 'Droplets', value: 645.20, type: 'service', parent: 'digitalocean', color: '#0080FF', trend: 3.5, anomaly: false, description: 'Compute VMs' },
          { id: 'do-spaces', label: 'Spaces', value: 180.30, type: 'service', parent: 'digitalocean', color: '#0080FF', trend: 6.2, anomaly: false, description: 'Object Storage' },
          { id: 'do-k8s', label: 'Kubernetes', value: 120.00, type: 'service', parent: 'digitalocean', color: '#0080FF', trend: 1.0, anomaly: false, description: 'DOKS Clusters' },
          // EC2 Breakdown
          { id: 'ec2-prod', label: 'Production', value: 2100.00, type: 'environment', parent: 'ec2', color: '#10B981', trend: 12.0 },
          { id: 'ec2-staging', label: 'Staging', value: 745.67, type: 'environment', parent: 'ec2', color: '#F59E0B', trend: 8.5 },
          { id: 'ec2-dev', label: 'Development', value: 400.00, type: 'environment', parent: 'ec2', color: '#6366F1', trend: 35.0, anomaly: true },
          // RDS Breakdown
          { id: 'rds-primary', label: 'Primary DB', value: 1500.00, type: 'resource', parent: 'rds', color: '#FF9900' },
          { id: 'rds-replica', label: 'Read Replica', value: 634.89, type: 'resource', parent: 'rds', color: '#FF9900' },
          // Instance types
          { id: 'ec2-prod-m5', label: 'm5.xlarge (x3)', value: 1200.00, type: 'instance', parent: 'ec2-prod', color: '#10B981' },
          { id: 'ec2-prod-c5', label: 'c5.2xlarge (x2)', value: 900.00, type: 'instance', parent: 'ec2-prod', color: '#10B981' },
        ],
        edges: [
          { from: 'root', to: 'aws', value: 7182.35 },
          { from: 'root', to: 'azure', value: 2134.89 },
          { from: 'root', to: 'gcp', value: 1092.26 },
          { from: 'root', to: 'snowflake', value: 4000.00 },
          { from: 'root', to: 'databricks', value: 2850.00 },
          { from: 'root', to: 'digitalocean', value: 945.50 },
          { from: 'aws', to: 'ec2', value: 3245.67 },
          { from: 'aws', to: 'rds', value: 2134.89 },
          { from: 'aws', to: 's3', value: 1234.56 },
          { from: 'aws', to: 'lambda', value: 567.23 },
          { from: 'azure', to: 'vms', value: 987.65 },
          { from: 'azure', to: 'sql', value: 567.24 },
          { from: 'azure', to: 'storage', value: 580.00 },
          { from: 'gcp', to: 'bigquery', value: 654.32 },
          { from: 'gcp', to: 'compute', value: 437.94 },
          { from: 'snowflake', to: 'sf-warehouse', value: 2500.00 },
          { from: 'snowflake', to: 'sf-storage', value: 1000.00 },
          { from: 'snowflake', to: 'sf-transfer', value: 500.00 },
          { from: 'databricks', to: 'db-allpurpose', value: 1450.00 },
          { from: 'databricks', to: 'db-jobs', value: 980.00 },
          { from: 'databricks', to: 'db-sqlwh', value: 420.00 },
          { from: 'digitalocean', to: 'do-droplets', value: 645.20 },
          { from: 'digitalocean', to: 'do-spaces', value: 180.30 },
          { from: 'digitalocean', to: 'do-k8s', value: 120.00 },
          { from: 'ec2', to: 'ec2-prod', value: 2100.00 },
          { from: 'ec2', to: 'ec2-staging', value: 745.67 },
          { from: 'ec2', to: 'ec2-dev', value: 400.00 },
          { from: 'rds', to: 'rds-primary', value: 1500.00 },
          { from: 'rds', to: 'rds-replica', value: 634.89 },
          { from: 'ec2-prod', to: 'ec2-prod-m5', value: 1200.00 },
          { from: 'ec2-prod', to: 'ec2-prod-c5', value: 900.00 },
        ],
        insights: [
          { text: 'Databricks all-purpose compute up 28.3% — likely Photon enabled where it shouldn\'t be', type: 'critical', savings: 580 },
          { text: 'Snowflake warehouse spend up 18.2% — check auto-suspend settings', type: 'warning', savings: 380 },
          { text: 'EC2 costs spiked 15.2% - investigate unused instances', type: 'warning', savings: 450 },
          { text: 'GCP BigQuery shows 18.5% increase - check query optimization', type: 'warning', savings: 120 },
          { text: 'Development environment up 35% - consider auto-shutdown', type: 'critical', savings: 280 },
          { text: 'AWS dominates spend at 41% - evaluate multi-cloud strategy', type: 'info' },
          { text: 'Reserved Instance coverage: 45% - potential $2,100/mo savings', type: 'opportunity', savings: 2100 },
        ],
        recommendations: [
          { action: 'Move Databricks scheduled jobs to Job Compute (2.5x cheaper)', impact: '$580/mo', confidence: 94 },
          { action: 'Right-size EC2 instances', impact: '$450/mo', confidence: 92 },
          { action: 'Set Snowflake auto-suspend to 60s', impact: '$380/mo', confidence: 95 },
          { action: 'Enable S3 Intelligent Tiering', impact: '$180/mo', confidence: 88 },
          { action: 'Purchase Reserved Instances', impact: '$2,100/mo', confidence: 95 },
          { action: 'Shut down dev instances nights/weekends', impact: '$280/mo', confidence: 90 },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const getNodeIcon = (type) => {
    const icons = {
      total: DollarSign,
      provider: Cloud,
      service: Server,
      database: Database,
      environment: Layers,
      resource: Box,
      instance: Cpu,
    };
    return icons[type] || HardDrive;
  };

  const getNodeColor = (node) => {
    if (node.color) return node.color;
    const colors = {
      total: '#10B981',
      provider: '#6366F1',
      service: '#F59E0B',
      environment: '#EC4899',
      resource: '#8B5CF6',
      instance: '#06B6D4',
    };
    return colors[node.type] || '#64748B';
  };

  const filteredNodes = useMemo(() => {
    if (!graphData) return [];
    return graphData.nodes.filter(node => {
      if (filterProvider !== 'all') {
        const providerNode = graphData.nodes.find(n => n.id === filterProvider);
        if (!providerNode) return false;
        // Check if node is under this provider
        let current = node;
        let isUnderProvider = false;
        while (current) {
          if (current.id === filterProvider || current.parent === filterProvider) {
            isUnderProvider = true;
            break;
          }
          current = graphData.nodes.find(n => n.id === current.parent);
        }
        if (!isUnderProvider && node.type !== 'total' && node.id !== filterProvider) return false;
      }
      if (node.value < filterMinCost) return false;
      return true;
    });
  }, [graphData, filterProvider, filterMinCost]);

  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const drillInto = (node) => {
    setDrillPath(prev => [...prev, node]);
    setSelectedNode(node);
    setExpandedNodes(prev => new Set([...prev, node.id]));
  };

  const drillBack = (index) => {
    setDrillPath(prev => prev.slice(0, index));
    if (index > 0) {
      setSelectedNode(drillPath[index - 1]);
    } else {
      setSelectedNode(null);
    }
  };

  // Interactive Force-Directed Graph View
  const renderInteractiveView = () => {
    if (!graphData) return null;
    
    const root = graphData.nodes.find(n => n.type === 'total');
    const displayRoot = drillPath.length > 0 ? drillPath[drillPath.length - 1] : root;
    
    const getChildren = (nodeId) => filteredNodes.filter(n => n.parent === nodeId);
    
    const NodeCircle = ({ node, x, y, radius, depth = 0 }) => {
      const Icon = getNodeIcon(node.type);
      const color = getNodeColor(node);
      const children = getChildren(node.id);
      const isExpanded = expandedNodes.has(node.id);
      const isSelected = selectedNode?.id === node.id;
      const isHovered = hoveredNode?.id === node.id;
      const hasAnomaly = node.anomaly;
      
      // Pulse animation for anomalies
      const pulseScale = hasAnomaly ? 1 + Math.sin(animationStep * 0.1) * 0.05 : 1;
      
      return (
        <g>
          {/* Connection lines to children */}
          {isExpanded && children.map((child, i) => {
            const angle = (i / children.length) * Math.PI * 2 - Math.PI / 2;
            const childRadius = radius * 0.4;
            const distance = radius * 1.8;
            const cx = x + Math.cos(angle) * distance;
            const cy = y + Math.sin(angle) * distance;
            
            // Animated flow line
            const flowOffset = animating ? (animationStep * 2) % 20 : 0;
            
            return (
              <g key={child.id}>
                <line
                  x1={x}
                  y1={y}
                  x2={cx}
                  y2={cy}
                  stroke={color}
                  strokeWidth={Math.max(1, (child.value / node.value) * 8)}
                  strokeOpacity={0.3}
                  strokeDasharray={animating ? "10,10" : "none"}
                  strokeDashoffset={flowOffset}
                  className="transition-all duration-300"
                />
                {/* Animated particles */}
                {animating && (
                  <circle
                    cx={x + (cx - x) * ((animationStep % 50) / 50)}
                    cy={y + (cy - y) * ((animationStep % 50) / 50)}
                    r={3}
                    fill={color}
                    opacity={0.8}
                  />
                )}
                <NodeCircle 
                  node={child} 
                  x={cx} 
                  y={cy} 
                  radius={childRadius} 
                  depth={depth + 1}
                />
              </g>
            );
          })}
          
          {/* Anomaly glow */}
          {hasAnomaly && (
            <circle
              cx={x}
              cy={y}
              r={radius * 1.3 * pulseScale}
              fill="none"
              stroke="#EF4444"
              strokeWidth={2}
              strokeOpacity={0.5}
              className="animate-pulse"
            />
          )}
          
          {/* Main node circle */}
          <circle
            cx={x}
            cy={y}
            r={radius * (isHovered ? 1.1 : 1) * pulseScale}
            fill={isSelected ? color : `${color}CC`}
            stroke={isSelected ? '#fff' : color}
            strokeWidth={isSelected ? 4 : 2}
            className="cursor-pointer transition-all duration-200 drop-shadow-lg"
            style={{ filter: isHovered ? 'brightness(1.2)' : 'none' }}
            onClick={() => setSelectedNode(node)}
            onMouseEnter={() => setHoveredNode(node)}
            onMouseLeave={() => setHoveredNode(null)}
            onDoubleClick={() => children.length > 0 && drillInto(node)}
          />
          
          {/* Icon */}
          <foreignObject
            x={x - radius * 0.35}
            y={y - radius * 0.5}
            width={radius * 0.7}
            height={radius * 0.7}
            className="pointer-events-none"
          >
            <div className="w-full h-full flex items-center justify-center">
              <Icon className="text-white" style={{ width: radius * 0.4, height: radius * 0.4 }} />
            </div>
          </foreignObject>
          
          {/* Labels */}
          {showLabels && (
            <>
              <text
                x={x}
                y={y + radius * 0.15}
                textAnchor="middle"
                className="fill-white font-bold pointer-events-none"
                style={{ fontSize: Math.max(10, radius * 0.2) }}
              >
                {node.label}
              </text>
              <text
                x={x}
                y={y + radius * 0.4}
                textAnchor="middle"
                className="fill-white/80 pointer-events-none"
                style={{ fontSize: Math.max(8, radius * 0.15) }}
              >
                ${node.value?.toLocaleString()}
              </text>
            </>
          )}
          
          {/* Trend indicator */}
          {node.trend !== undefined && (
            <g transform={`translate(${x + radius * 0.7}, ${y - radius * 0.7})`}>
              <circle r={12} fill={node.trend > 0 ? '#EF4444' : '#10B981'} />
              {node.trend > 0 ? (
                <TrendingUp className="text-white" style={{ transform: 'translate(-6px, -6px)', width: 12, height: 12 }} />
              ) : (
                <TrendingDown className="text-white" style={{ transform: 'translate(-6px, -6px)', width: 12, height: 12 }} />
              )}
            </g>
          )}
          
          {/* Expand/collapse indicator */}
          {children.length > 0 && (
            <g 
              transform={`translate(${x + radius * 0.8}, ${y + radius * 0.8})`}
              className="cursor-pointer"
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }}
            >
              <circle r={14} fill="#1E293B" stroke={color} strokeWidth={2} />
              {isExpanded ? (
                <ChevronDown className="text-white" style={{ transform: 'translate(-6px, -6px)', width: 12, height: 12 }} />
              ) : (
                <ChevronRight className="text-white" style={{ transform: 'translate(-6px, -6px)', width: 12, height: 12 }} />
              )}
              <text
                x={0}
                y={24}
                textAnchor="middle"
                className="fill-slate-400 text-xs pointer-events-none"
              >
                {children.length}
              </text>
            </g>
          )}
        </g>
      );
    };
    
    return (
      <div className="relative w-full h-[600px] bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl overflow-hidden">
        {/* Background grid */}
        <svg className="absolute inset-0 w-full h-full opacity-10">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        
        <svg 
          viewBox="0 0 800 600" 
          className="w-full h-full"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
        >
          {displayRoot && (
            <NodeCircle 
              node={displayRoot} 
              x={400} 
              y={300} 
              radius={80}
            />
          )}
        </svg>
        
        {/* Animation controls */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-slate-800/80 backdrop-blur rounded-lg p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAnimating(!animating)}
            className="text-white hover:bg-slate-700"
          >
            {animating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <span className="text-xs text-slate-400 dark:text-slate-400">Flow Animation</span>
        </div>
        
        {/* Legend */}
        <div className="absolute top-4 right-4 bg-slate-800/80 backdrop-blur rounded-lg p-3 space-y-2">
          <div className="text-xs font-medium text-white mb-2">Legend</div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full bg-[#FF9900]" /> AWS
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full bg-[#0078D4]" /> Azure
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full bg-[#4285F4]" /> GCP
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full bg-[#29B5E8]" /> Snowflake
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full bg-[#FF3621]" /> Databricks
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full bg-[#0080FF]" /> DigitalOcean
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <div className="w-3 h-3 rounded-full border-2 border-red-500" /> Anomaly
          </div>
        </div>
      </div>
    );
  };

  // Tree View with full interactivity
  const renderTreeView = () => {
    if (!graphData) return null;

    const TreeNode = ({ node, depth = 0 }) => {
      const Icon = getNodeIcon(node.type);
      const color = getNodeColor(node);
      const children = filteredNodes.filter(n => n.parent === node.id);
      const isSelected = selectedNode?.id === node.id;
      const isExpanded = expandedNodes.has(node.id);
      const root = graphData.nodes.find(n => n.type === 'total');
      const percentage = root ? ((node.value / root.value) * 100).toFixed(1) : 100;

      return (
        <div className={`${depth > 0 ? 'ml-6' : ''}`}>
          <div
            onClick={() => setSelectedNode(node)}
            className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-2 ${
              isSelected 
                ? 'bg-emerald-500/20 ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20' 
                : 'bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 hover:shadow-md'
            }`}
            style={{ borderLeft: `4px solid ${color}` }}
          >
            {children.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }}
                className="p-1 hover:bg-slate-600 rounded transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-400" />
                )}
              </button>
            )}
            {children.length === 0 && <div className="w-6" />}
            
            <div className="p-2 rounded-lg transition-transform group-hover:scale-110" style={{ backgroundColor: `${color}30` }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-white truncate">{node.label}</span>
                {node.anomaly && (
                  <Badge className="bg-red-500/20 text-red-400 text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Anomaly
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-slate-400 dark:text-slate-400">${node.value?.toLocaleString()}</span>
                {node.trend !== undefined && (
                  <span className={`flex items-center gap-1 ${node.trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {node.trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {Math.abs(node.trend)}%
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-20 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%`, backgroundColor: color }}
                />
              </div>
              <Badge variant="outline" className="text-slate-200 border-emerald-500/30 dark:border-slate-600">
                {percentage}%
              </Badge>
            </div>
            
            {children.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); drillInto(node); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 dark:text-slate-400 hover:text-white dark:hover:text-white"
              >
                <Target className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {isExpanded && children.length > 0 && (
            <div className="border-l-2 border-dashed border-emerald-500/20/50 ml-5 pl-2 space-y-1">
              {children
                .sort((a, b) => b.value - a.value)
                .map(child => (
                  <TreeNode key={child.id} node={child} depth={depth + 1} />
                ))}
            </div>
          )}
        </div>
      );
    };

    const displayRoot = drillPath.length > 0 
      ? drillPath[drillPath.length - 1] 
      : graphData.nodes.find(n => n.type === 'total');

    return (
      <div className="space-y-4" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
        {displayRoot && <TreeNode node={displayRoot} />}
      </div>
    );
  };

  // Sankey Diagram View
  const renderSankeyView = () => {
    if (!graphData) return null;

    const levels = [
      { title: 'Total Spend', nodes: filteredNodes.filter(n => n.type === 'total') },
      { title: 'Cloud Providers', nodes: filteredNodes.filter(n => n.type === 'provider') },
      { title: 'Services', nodes: filteredNodes.filter(n => n.type === 'service') },
      { title: 'Resources', nodes: filteredNodes.filter(n => ['environment', 'resource'].includes(n.type)) },
    ].filter(l => l.nodes.length > 0);

    const root = graphData.nodes.find(n => n.type === 'total');

    return (
      <div className="relative overflow-x-auto pb-4">
        <div className="flex gap-8 min-w-[1000px] p-4" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
          {levels.map((level, levelIndex) => (
            <div key={level.title} className="flex-1">
              <h4 className="text-sm font-semibold text-slate-400 dark:text-slate-400 mb-4 text-center uppercase tracking-wider">
                {level.title}
              </h4>
              <div className="space-y-3">
                {level.nodes.sort((a, b) => b.value - a.value).map(node => {
                  const Icon = getNodeIcon(node.type);
                  const color = getNodeColor(node);
                  const percentage = root ? (node.value / root.value) * 100 : 100;
                  const isSelected = selectedNode?.id === node.id;
                  
                  return (
                    <div
                      key={node.id}
                      onClick={() => setSelectedNode(node)}
                      className={`relative p-4 rounded-xl cursor-pointer transition-all overflow-hidden ${
                        isSelected
                          ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20'
                          : 'hover:shadow-lg'
                      }`}
                      style={{ 
                        backgroundColor: `${color}15`,
                        borderLeft: `4px solid ${color}`,
                        minHeight: `${Math.max(80, percentage * 3)}px`,
                      }}
                    >
                      {/* Animated background */}
                      {animating && (
                        <div 
                          className="absolute inset-0 opacity-20"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                            transform: `translateX(${(animationStep % 100) - 50}%)`,
                          }}
                        />
                      )}
                      
                      <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-5 h-5" style={{ color }} />
                          <span className="font-medium text-white">{node.label}</span>
                          {node.anomaly && (
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                        <div className="text-lg font-bold text-white">
                          ${node.value?.toLocaleString()}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-slate-400 dark:text-slate-400">{percentage.toFixed(1)}%</span>
                          {node.trend !== undefined && (
                            <span className={`flex items-center gap-1 text-sm ${node.trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                              {node.trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {Math.abs(node.trend)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        
        {/* Flow lines connecting levels */}
        <svg className="absolute inset-0 pointer-events-none" style={{ minWidth: '1000px' }}>
          {/* SVG flow connections would go here */}
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-96 bg-slate-900 dark:bg-slate-900">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-emerald-500/30 rounded-full animate-spin border-t-emerald-500 mx-auto" />
            <Network className="w-6 h-6 text-emerald-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-slate-400 dark:text-slate-400">Loading Cost Intelligence Graph...</p>
        </div>
      </div>
    );
  }

  if (!shouldShowData) return <EmptyDataState title="Cost Intelligence Graph" description="Connect your cloud accounts to visualize cost flows across providers and services." />;
  return (
    <div className="min-h-screen bg-slate-900 dark:bg-slate-900 p-6 space-y-6" data-testid="cost-intelligence-graph-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-xl">
              <Network className="w-7 h-7 text-emerald-400" />
            </div>
            Cost Intelligence Graph™
            <Badge className="bg-emerald-500/20 text-emerald-400">Interactive</Badge>
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Visualize and explore cost relationships across your cloud infrastructure
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Time range selector */}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36 bg-slate-900 dark:bg-slate-800 border-emerald-500/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mtd">This Month</SelectItem>
              <SelectItem value="last30">Last 30 Days</SelectItem>
              <SelectItem value="last90">Last 90 Days</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Filter toggle */}
          <Button 
            variant={showFilters ? "default" : "outline"} 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "bg-emerald-600" : "border-emerald-500/20 text-slate-200"}
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
          </Button>
          
          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
            <Button variant="ghost" size="icon" onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} className="h-8 w-8 text-slate-400 dark:text-slate-400 hover:text-white dark:hover:text-white">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-400 dark:text-slate-400 w-12 text-center">{(zoom * 100).toFixed(0)}%</span>
            <Button variant="ghost" size="icon" onClick={() => setZoom(Math.min(2, zoom + 0.1))} className="h-8 w-8 text-slate-400 dark:text-slate-400 hover:text-white dark:hover:text-white">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setZoom(1)} className="h-8 w-8 text-slate-400 dark:text-slate-400 hover:text-white dark:hover:text-white">
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchGraphData} className="border-emerald-500/20 text-slate-200">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-slate-800/50 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm text-slate-400 dark:text-slate-400 mb-2 block">Cloud Provider</label>
                <Select value={filterProvider} onValueChange={setFilterProvider}>
                  <SelectTrigger className="bg-slate-900 border-emerald-500/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    <SelectItem value="aws">AWS</SelectItem>
                    <SelectItem value="azure">Azure</SelectItem>
                    <SelectItem value="gcp">GCP</SelectItem>
                    <SelectItem value="snowflake">Snowflake</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 dark:text-slate-400 mb-2 block">Min Cost: ${filterMinCost}</label>
                <Slider
                  value={[filterMinCost]}
                  onValueChange={([val]) => setFilterMinCost(val)}
                  max={5000}
                  step={100}
                  className="mt-3"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  checked={showLabels}
                  onCheckedChange={setShowLabels}
                />
                <label className="text-sm text-slate-200">Show Labels</label>
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  checked={animating}
                  onCheckedChange={setAnimating}
                />
                <label className="text-sm text-slate-200">Animate Flows</label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Breadcrumb / Drill Path */}
      {drillPath.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => drillBack(0)}
            className="text-slate-400 dark:text-slate-400 hover:text-white dark:hover:text-white"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <span className="text-slate-400">/</span>
          {drillPath.map((node, i) => (
            <React.Fragment key={node.id}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => drillBack(i + 1)}
                className="text-emerald-400 hover:text-emerald-300"
              >
                {node.label}
              </Button>
              {i < drillPath.length - 1 && <ChevronRight className="w-4 h-4 text-slate-400" />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="bg-slate-800 border border-emerald-500/20">
          <TabsTrigger value="interactive" className="data-[state=active]:bg-emerald-600" data-testid="view-interactive">
            <Network className="w-4 h-4 mr-2" />
            Interactive
          </TabsTrigger>
          <TabsTrigger value="tree" className="data-[state=active]:bg-emerald-600" data-testid="view-tree">
            <GitBranch className="w-4 h-4 mr-2" />
            Hierarchy
          </TabsTrigger>
          <TabsTrigger value="sankey" className="data-[state=active]:bg-emerald-600" data-testid="view-sankey">
            <Activity className="w-4 h-4 mr-2" />
            Flow
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Graph */}
        <Card className="lg:col-span-2 bg-slate-800/50 border-emerald-500/20 overflow-hidden">
          <CardContent className="p-0">
            {viewMode === 'interactive' && renderInteractiveView()}
            {viewMode === 'tree' && <div className="p-6 min-h-[600px]">{renderTreeView()}</div>}
            {viewMode === 'sankey' && <div className="p-6 min-h-[600px]">{renderSankeyView()}</div>}
          </CardContent>
        </Card>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Selected Node Details */}
          {selectedNode ? (
            <Card className="bg-slate-800/50 border-emerald-500/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-white">
                    {React.createElement(getNodeIcon(selectedNode.type), { 
                      className: "w-5 h-5",
                      style: { color: getNodeColor(selectedNode) }
                    })}
                    {selectedNode.label}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedNode(null)} className="text-slate-400 dark:text-slate-400">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-slate-400 dark:text-slate-400 border-emerald-500/30 dark:border-slate-600">{selectedNode.type}</Badge>
                  {selectedNode.anomaly && (
                    <Badge className="bg-red-500/20 text-red-400">Anomaly Detected</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <div className="text-xs text-slate-400 dark:text-slate-400 mb-1">Cost</div>
                    <div className="text-lg font-bold text-white">${selectedNode.value?.toLocaleString()}</div>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <div className="text-xs text-slate-400 dark:text-slate-400 mb-1">Trend</div>
                    <div className={`text-lg font-bold flex items-center gap-1 ${selectedNode.trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {selectedNode.trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {Math.abs(selectedNode.trend || 0)}%
                    </div>
                  </div>
                </div>
                
                {selectedNode.description && (
                  <p className="text-sm text-slate-400 dark:text-slate-400">{selectedNode.description}</p>
                )}
                
                {/* Children breakdown */}
                {filteredNodes.filter(n => n.parent === selectedNode.id).length > 0 && (
                  <div className="pt-3 border-t border-emerald-500/20">
                    <h4 className="text-sm font-medium text-slate-200 mb-3">Breakdown</h4>
                    <div className="space-y-2">
                      {filteredNodes
                        .filter(n => n.parent === selectedNode.id)
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 5)
                        .map(child => (
                          <div 
                            key={child.id} 
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                            onClick={() => setSelectedNode(child)}
                          >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getNodeColor(child) }} />
                            <span className="flex-1 text-sm text-slate-200">{child.label}</span>
                            <span className="text-sm text-slate-400 dark:text-slate-400">${child.value?.toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {filteredNodes.filter(n => n.parent === selectedNode.id).length > 0 && (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => drillInto(selectedNode)}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Drill Into {selectedNode.label}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-800/50 border-emerald-500/20">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Eye className="w-6 h-6 text-slate-400 dark:text-slate-400" />
                </div>
                <p className="text-slate-400 dark:text-slate-400 text-sm">Click on any node to view details</p>
                <p className="text-slate-400 text-xs mt-1">Double-click to drill down</p>
              </CardContent>
            </Card>
          )}

          {/* AI Insights */}
          <Card className="bg-slate-800/50 border-emerald-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                AI Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {graphData?.insights?.slice(0, 4).map((insight, i) => (
                  <li 
                    key={i} 
                    className={`flex items-start gap-3 p-2 rounded-lg ${
                      insight.type === 'critical' ? 'bg-red-500/10' :
                      insight.type === 'warning' ? 'bg-amber-500/10' :
                      insight.type === 'opportunity' ? 'bg-emerald-500/10' :
                      'bg-slate-700/30'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      insight.type === 'critical' ? 'bg-red-500' :
                      insight.type === 'warning' ? 'bg-amber-500' :
                      insight.type === 'opportunity' ? 'bg-emerald-500' :
                      'bg-emerald-500'
                    }`} />
                    <div className="flex-1">
                      <span className="text-sm text-slate-200">{insight.text}</span>
                      {insight.savings && (
                        <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 text-xs">
                          Save ${insight.savings}
                        </Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="bg-slate-800/50 border-emerald-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Target className="w-4 h-4 text-emerald-400" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {graphData?.recommendations?.slice(0, 3).map((rec, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700/50 cursor-pointer transition-colors">
                  <div>
                    <div className="text-sm font-medium text-white">{rec.action}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-400">Impact: {rec.impact}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-emerald-400">{rec.confidence}%</div>
                    <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-400" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bg-slate-800/50 border-emerald-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white">Graph Stats</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <div className="text-2xl font-bold text-white">{filteredNodes.length}</div>
                <div className="text-xs text-slate-400 dark:text-slate-400">Nodes</div>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <div className="text-2xl font-bold text-white">{graphData?.edges?.length || 0}</div>
                <div className="text-xs text-slate-400 dark:text-slate-400">Connections</div>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <div className="text-2xl font-bold text-emerald-400">
                  {filteredNodes.filter(n => n.type === 'provider').length}
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-400">Providers</div>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <div className="text-2xl font-bold text-amber-400">
                  {filteredNodes.filter(n => n.anomaly).length}
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-400">Anomalies</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CostIntelligenceGraph;
